import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { DatePipe } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil, last } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

export interface TicketComment {
  id?: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  message: string;
  createdAt: Date | any;
  isAdmin?: boolean;
  attachments?: string[];
}

export interface TicketAttachment {
  name: string;
  url: string;
  size: number;
  type: string;
}

export interface Ticket {
  id?: string;
  ticketId: string; // e.g., #TKT-2401
  subject: string;
  description: string;
  category: 'Bug Report' | 'Feature Request' | 'Support' | 'Technical Issue' | 'Account Management' | string;
  priority: 'Low' | 'Normal' | 'High' | 'Urgent' | string;
  status: 'Open' | 'In Progress' | 'Solved' | 'Closed' | string;
  requesterId: string;
  requesterName: string;
  requesterEmail: string;
  requesterAvatar?: string;
  createdAt: Date | any;
  updatedAt: Date | any;
  comments?: TicketComment[];
  attachments?: TicketAttachment[];
  commentCount?: number;
  hasAttachment?: boolean;
}

@Component({
  selector: 'app-mycontacts',
  templateUrl: './my-contacts.component.html',
  styleUrls: ['./my-contacts.component.scss'],
  providers: [DatePipe, MessageService],
})
export class MyContactsComponent implements OnInit, OnDestroy {
  tickets: Ticket[] = [];
  filteredTickets: Ticket[] = [];
  selectedTicket: Ticket | null = null;
  loading = false;
  currentUser: any;
  private destroy$ = new Subject<void>();

  // Stats
  stats = {
    totalTickets: 0,
    openTickets: 0,
    inProgressTickets: 0,
    solvedToday: 0,
    avgResponseTime: '4.2h',
  };

  // Search & Filters
  searchQuery: string = '';
  activeStatusFilter: string = 'All Status';
  sortBy: string = 'Newest First';
  viewMode: 'list' | 'grid' = 'list';

  // Create Ticket Modal
  showCreateTicketModal = false;
  createTicketForm = {
    category: '',
    priority: 'Normal',
    subject: '',
    description: '',
    attachments: [] as File[],
    emailNotification: true,
  };

  // Ticket Detail Modal
  showTicketDetailModal = false;
  newComment: string = '';
  commentAttachments: File[] = [];
  editTicketForm = {
    status: '',
    priority: '',
    category: '',
  };

  // Ticket editing
  editingTicket = false;
  editTicketData = {
    subject: '',
    description: '',
    attachments: [] as File[],
    existingAttachments: [] as any[],
  };

  // Comment editing
  editingCommentId: string | null = null;
  editCommentMessage: string = '';
  editCommentAttachments: File[] = [];
  editCommentExistingAttachments: any[] = [];

  // Loading states
  creatingTicket = false;
  addingComment = false;
  editingComment = false;
  deletingCommentId: string | null = null; // Track which comment is being deleted
  updatingTicket = false;

  // Pagination
  first = 0;
  rows = 10;
  totalRecords = 0;

  // Bulk selection
  selectedTickets: Ticket[] = [];
  selectAll = false;

  constructor(
    private afs: AngularFirestore,
    private afAuth: AngularFireAuth,
    private storage: AngularFireStorage,
    private datePipe: DatePipe,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.getCurrentUser();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCurrentUser() {
    this.loading = true;
    this.afAuth.authState.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (user) {
        this.afs
          .collection('users')
          .doc(user.uid)
          .valueChanges()
          .pipe(takeUntil(this.destroy$))
          .subscribe((userData: any) => {
            this.currentUser = {
              ...userData,
              uid: user.uid,
              role: userData?.role || 'company', // Default to company if role not set
            };
          this.loading = false;
            this.loadTickets();
        });
      } else {
        this.loading = false;
      }
    });
  }

  loadTickets() {
    if (!this.currentUser?.uid) {
      this.tickets = [];
      this.filteredTickets = [];
    return;
  }

    this.afs
      .collection('contacts', (ref) =>
        ref.where('requesterId', '==', this.currentUser.uid).orderBy('createdAt', 'desc')
      )
      .snapshotChanges()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
    next: (res: any) => {
          this.tickets = res.map((e: any) => {
            const data = e.payload.doc.data();
            return {
        id: e.payload.doc.id,
              ...data,
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
              updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
              commentCount: data.comments?.length || 0,
              hasAttachment: data.attachments && data.attachments.length > 0,
            };
          });
          this.calculateStats();
          this.applyFilters();
        },
        error: (err) => {
          console.error('Error fetching tickets:', err);
          this.tickets = [];
          this.filteredTickets = [];
        },
      });
  }

  calculateStats() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.stats.totalTickets = this.tickets.length;
    this.stats.openTickets = this.tickets.filter((t) => t.status === 'Open').length;
    this.stats.inProgressTickets = this.tickets.filter((t) => t.status === 'In Progress').length;
    this.stats.solvedToday = this.tickets.filter((t) => {
      if (t.status === 'Solved' && t.updatedAt) {
        const solvedDate = t.updatedAt instanceof Date ? t.updatedAt : new Date(t.updatedAt);
        return solvedDate >= today;
      }
      return false;
    }).length;
  }

  applyFilters() {
    let filtered = [...this.tickets];

    // Apply status filter
    if (this.activeStatusFilter !== 'All Status') {
      filtered = filtered.filter((ticket) => ticket.status === this.activeStatusFilter);
    }

    // Apply search filter
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (ticket) =>
          ticket.subject.toLowerCase().includes(query) ||
          ticket.description.toLowerCase().includes(query) ||
          ticket.ticketId.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered = this.sortTickets(filtered);

    this.filteredTickets = filtered;
    this.totalRecords = filtered.length;
  }

  sortTickets(tickets: Ticket[]): Ticket[] {
    const sorted = [...tickets];
    switch (this.sortBy) {
      case 'Newest First':
        return sorted.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
      case 'Oldest First':
        return sorted.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateA.getTime() - dateB.getTime();
        });
      case 'Priority: High to Low':
        const priorityOrder: { [key: string]: number } = { Urgent: 4, High: 3, Normal: 2, Low: 1 };
        return sorted.sort((a, b) => (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0));
      case 'Priority: Low to High':
        const priorityOrderLow: { [key: string]: number } = { Urgent: 4, High: 3, Normal: 2, Low: 1 };
        return sorted.sort((a, b) => (priorityOrderLow[a.priority] || 0) - (priorityOrderLow[b.priority] || 0));
      case 'Recently Updated':
        return sorted.sort((a, b) => {
          const dateA = a.updatedAt instanceof Date ? a.updatedAt : new Date(a.updatedAt || a.createdAt);
          const dateB = b.updatedAt instanceof Date ? b.updatedAt : new Date(b.updatedAt || b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
      default:
        return sorted;
    }
  }

  onSearch() {
    this.applyFilters();
  }

  onStatusFilterChange(status: string) {
    this.activeStatusFilter = status;
    this.applyFilters();
  }

  onSortChange() {
    this.applyFilters();
  }

  onViewModeChange(mode: 'list' | 'grid') {
    this.viewMode = mode;
  }

  // Create Ticket
  openCreateTicketModal() {
    this.createTicketForm = {
      category: '',
      priority: 'Normal',
      subject: '',
      description: '',
      attachments: [],
      emailNotification: true,
    };
    this.showCreateTicketModal = true;
  }

  closeCreateTicketModal() {
    this.showCreateTicketModal = false;
  }

  onFileSelect(event: any) {
    const files = Array.from(event.target.files) as File[];
    this.createTicketForm.attachments = [...this.createTicketForm.attachments, ...files];
  }

  removeAttachment(index: number) {
    this.createTicketForm.attachments.splice(index, 1);
  }

  async createTicket() {
    if (!this.createTicketForm.subject || !this.createTicketForm.description || !this.createTicketForm.category) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
      });
      return;
    }

    if (this.creatingTicket) return; // Prevent double submission

    this.creatingTicket = true;
    try {
      // Generate ticket ID
      const ticketCount = this.tickets.length;
      const ticketId = `#TKT-${2400 + ticketCount + 1}`;

      // Upload attachments
      const uploadedAttachments: TicketAttachment[] = [];
      for (const file of this.createTicketForm.attachments) {
        const filePath = `tickets/${this.currentUser.uid}/${Date.now()}_${file.name}`;
        const fileRef = this.storage.ref(filePath);
        const task = fileRef.put(file);
        await task.snapshotChanges().pipe(last(), takeUntil(this.destroy$)).toPromise();
        const url = await fileRef.getDownloadURL().toPromise();
        uploadedAttachments.push({
          name: file.name,
          url: url as string,
          size: file.size,
          type: file.type,
        });
      }

      // Get requester name based on role
      let requesterName = 'Company User';
      if (this.currentUser.role === 'company') {
        requesterName = this.currentUser.name || 'Company User';
      } else if (this.currentUser.fname || this.currentUser.lname) {
        requesterName = `${this.currentUser.fname || ''} ${this.currentUser.lname || ''}`.trim() || 'User';
      }

      // Get requester avatar based on role
      let requesterAvatar: string | undefined;
      if (this.currentUser.role === 'company') {
        requesterAvatar = this.currentUser.logo;
      } else {
        requesterAvatar = this.currentUser.avatar || this.currentUser.photoURL;
      }

      const ticketData: any = {
        ticketId,
        subject: this.createTicketForm.subject,
        description: this.createTicketForm.description,
        category: this.createTicketForm.category,
        priority: this.createTicketForm.priority,
        status: 'Open',
        requesterId: this.currentUser.uid,
        requesterName: requesterName,
        requesterEmail: this.currentUser.email,
        createdAt: new Date(),
        updatedAt: new Date(),
        comments: [],
        attachments: uploadedAttachments,
        commentCount: 0,
        hasAttachment: uploadedAttachments.length > 0,
      };

      // Only add requesterAvatar if it exists
      if (requesterAvatar) {
        ticketData.requesterAvatar = requesterAvatar;
      }

      await this.afs.collection('contacts').add(ticketData);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Ticket created successfully',
      });

      this.closeCreateTicketModal();
    } catch (error) {
      console.error('Error creating ticket:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to create ticket',
      });
    } finally {
      this.creatingTicket = false;
    }
  }

  // Ticket Detail Modal
  openTicketDetailModal(ticket: Ticket) {
    this.selectedTicket = { ...ticket };
    this.editTicketForm = {
      status: ticket.status,
      priority: ticket.priority,
      category: ticket.category,
    };
    this.newComment = '';
    this.commentAttachments = [];
    this.showTicketDetailModal = true;
  }

  closeTicketDetailModal() {
    this.showTicketDetailModal = false;
    this.selectedTicket = null;
    this.editingTicket = false;
    this.editTicketData = {
      subject: '',
      description: '',
      attachments: [],
      existingAttachments: [],
    };
  }

  // Start editing ticket
  startEditTicket() {
    if (!this.selectedTicket) return;
    this.editingTicket = true;
    this.editTicketData = {
      subject: this.selectedTicket.subject,
      description: this.selectedTicket.description,
      attachments: [],
      existingAttachments: this.selectedTicket.attachments ? [...this.selectedTicket.attachments] : [],
    };
  }

  // Cancel editing ticket
  cancelEditTicket() {
    if (!this.selectedTicket) return;
    this.editingTicket = false;
    this.editTicketData = {
      subject: this.selectedTicket.subject,
      description: this.selectedTicket.description,
      attachments: [],
      existingAttachments: this.selectedTicket.attachments ? [...this.selectedTicket.attachments] : [],
    };
  }

  // Remove existing ticket attachment
  removeTicketAttachment(index: number) {
    this.editTicketData.existingAttachments.splice(index, 1);
  }

  // Add new ticket attachment
  onTicketFileSelect(event: any) {
    const files = Array.from(event.target.files) as File[];
    this.editTicketData.attachments = [...this.editTicketData.attachments, ...files];
  }

  // Remove new ticket attachment
  removeNewTicketAttachment(index: number) {
    this.editTicketData.attachments.splice(index, 1);
  }

  // Save edited ticket
  async saveEditTicket() {
    if (!this.selectedTicket?.id || !this.editTicketData.subject.trim() || !this.editTicketData.description.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Subject and description are required',
      });
      return;
    }

    if (this.updatingTicket) return;

    this.updatingTicket = true;
    try {
      // Upload new attachments if any
      let finalAttachments = [...this.editTicketData.existingAttachments];
      if (this.editTicketData.attachments.length > 0) {
        for (const file of this.editTicketData.attachments) {
          const filePath = `tickets/${this.currentUser.uid}/${Date.now()}_${file.name}`;
          const fileRef = this.storage.ref(filePath);
          const task = fileRef.put(file);
          await task.snapshotChanges().pipe(last(), takeUntil(this.destroy$)).toPromise();
          const url = await fileRef.getDownloadURL().toPromise();
          finalAttachments.push({
            name: file.name,
            url: url as string,
            size: file.size,
            type: file.type,
          });
        }
      }

      const updateData: any = {
        subject: this.editTicketData.subject,
        description: this.editTicketData.description,
        attachments: finalAttachments,
        hasAttachment: finalAttachments.length > 0,
        status: 'pending', // Set status to pending when ticket is edited (requires admin approval)
        priority: this.editTicketForm.priority,
        category: this.editTicketForm.category,
        updatedAt: new Date(),
      };

      // Add status change comment if status changed
      if (this.selectedTicket.status !== 'pending') {
        // Determine if user is admin
        const isAdmin = this.currentUser.role === 'superAdmin' || this.currentUser.role === 'admin';
        
        // Get user name based on role
        let userName = 'User';
        if (this.currentUser.role === 'company') {
          userName = this.currentUser.name || 'Company User';
        } else if (this.currentUser.fname || this.currentUser.lname) {
          userName = `${this.currentUser.fname || ''} ${this.currentUser.lname || ''}`.trim() || 'User';
        }

        // Get user avatar based on role
        let userAvatar: string | undefined;
        if (this.currentUser.role === 'company') {
          userAvatar = this.currentUser.logo;
        } else {
          userAvatar = this.currentUser.avatar || this.currentUser.photoURL;
        }

        const statusComment: any = {
          userId: this.currentUser.uid,
          userName: userName,
          message: `Ticket edited - Status changed to: Pending (requires admin approval)`,
          createdAt: new Date(),
          isAdmin: isAdmin,
        };

        // Only add userAvatar if it exists
        if (userAvatar) {
          statusComment.userAvatar = userAvatar;
        }

        const ticketRef = this.afs.collection('contacts').doc(this.selectedTicket.id);
        const ticketDoc = await ticketRef.get().toPromise();
        const ticketData = ticketDoc?.data() as Ticket;
        const updatedComments = [...(ticketData.comments || []), statusComment];

        updateData.comments = updatedComments;
        updateData.commentCount = updatedComments.length;
      }

      const ticketRef = this.afs.collection('contacts').doc(this.selectedTicket.id);
      await ticketRef.update(updateData);

      // Update selectedTicket immediately
      if (this.selectedTicket) {
        this.selectedTicket.subject = this.editTicketData.subject;
        this.selectedTicket.description = this.editTicketData.description;
        this.selectedTicket.attachments = finalAttachments;
        this.selectedTicket.hasAttachment = finalAttachments.length > 0;
        this.selectedTicket.status = 'pending';
        this.selectedTicket.priority = this.editTicketForm.priority;
        this.selectedTicket.category = this.editTicketForm.category;
        this.selectedTicket.updatedAt = new Date();
        
        // Update comments if status changed
        if (updateData.comments) {
          this.selectedTicket.comments = updateData.comments;
          this.selectedTicket.commentCount = updateData.commentCount;
        }
      }

      this.editingTicket = false;

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Ticket updated successfully',
      });

      // Reload tickets list
      this.loadTickets();
    } catch (error) {
      console.error('Error updating ticket:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update ticket',
      });
    } finally {
      this.updatingTicket = false;
    }
  }

  async addComment() {
    if (!this.selectedTicket?.id || !this.newComment.trim()) return;
    if (this.addingComment) return; // Prevent double submission

    this.addingComment = true;
    try {
      // Determine if user is admin
      const isAdmin = this.currentUser.role === 'superAdmin' || this.currentUser.role === 'admin';
      
      // Get user name based on role
      let userName = 'User';
      if (this.currentUser.role === 'company') {
        userName = this.currentUser.name || 'Company User';
      } else if (this.currentUser.fname || this.currentUser.lname) {
        userName = `${this.currentUser.fname || ''} ${this.currentUser.lname || ''}`.trim() || 'User';
      }

      // Get user avatar based on role
      let userAvatar: string | undefined;
      if (this.currentUser.role === 'company') {
        userAvatar = this.currentUser.logo;
      } else {
        userAvatar = this.currentUser.avatar || this.currentUser.photoURL;
      }

      const comment: any = {
        id: `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Generate unique ID
        userId: this.currentUser.uid,
        userName: userName,
        message: this.newComment,
        createdAt: new Date(),
        isAdmin: isAdmin,
        attachments: [],
      };

      // Only add userAvatar if it exists
      if (userAvatar) {
        comment.userAvatar = userAvatar;
      }

      // Upload comment attachments if any
      if (this.commentAttachments.length > 0) {
        const uploadedAttachments: any[] = [];
        for (const file of this.commentAttachments) {
          const filePath = `tickets/${this.currentUser.uid}/comments/${Date.now()}_${file.name}`;
          const fileRef = this.storage.ref(filePath);
          const task = fileRef.put(file);
          await task.snapshotChanges().pipe(last(), takeUntil(this.destroy$)).toPromise();
          const url = await fileRef.getDownloadURL().toPromise();
          uploadedAttachments.push({
            url: url as string,
            name: file.name,
            size: file.size,
            type: file.type,
          });
        }
        comment.attachments = uploadedAttachments;
      }

      const ticketRef = this.afs.collection('contacts').doc(this.selectedTicket.id);
      const ticketDoc = await ticketRef.get().toPromise();
      const ticketData = ticketDoc?.data() as Ticket;

      const updatedComments = [...(ticketData.comments || []), comment];

      await ticketRef.update({
        comments: updatedComments,
        commentCount: updatedComments.length,
        updatedAt: new Date(),
      });

      if (this.selectedTicket) {
        this.selectedTicket.comments = updatedComments;
        this.selectedTicket.commentCount = updatedComments.length;
      }

      this.newComment = '';
      this.commentAttachments = [];

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Comment added successfully',
      });
    } catch (error) {
      console.error('Error adding comment:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to add comment',
      });
    } finally {
      this.addingComment = false;
    }
  }

  onCommentFileSelect(event: any) {
    const files = Array.from(event.target.files) as File[];
    this.commentAttachments = [...this.commentAttachments, ...files];
  }

  removeCommentAttachment(index: number) {
    this.commentAttachments.splice(index, 1);
  }

  // Check if current user owns the comment
  isCommentOwner(comment: TicketComment): boolean {
    return comment.userId === this.currentUser?.uid;
  }

  // Get comment identifier for matching
  getCommentIdentifier(comment: TicketComment): string {
    if (comment.id) {
      return comment.id;
    }
    // Fallback: use userId and timestamp
    const timestamp = this.getCommentTimestamp(comment.createdAt);
    return `edit_${comment.userId}_${timestamp}`;
  }

  // Start editing a comment
  startEditComment(comment: TicketComment) {
    // Use ID if available, otherwise use a combination of userId and createdAt
    this.editingCommentId = comment.id || `edit_${comment.userId}_${this.getCommentTimestamp(comment.createdAt)}`;
    this.editCommentMessage = comment.message;
    this.editCommentAttachments = [];
    this.editCommentExistingAttachments = comment.attachments ? [...comment.attachments] : [];
  }

  // Cancel editing a comment
  cancelEditComment() {
    this.editingCommentId = null;
    this.editCommentMessage = '';
    this.editCommentAttachments = [];
    this.editCommentExistingAttachments = [];
    this.editingComment = false; // Reset editing state
  }

  // Remove existing attachment from comment being edited
  removeEditCommentAttachment(index: number) {
    this.editCommentExistingAttachments.splice(index, 1);
  }

  // Add new attachment to comment being edited
  onEditCommentFileSelect(event: any) {
    const files = Array.from(event.target.files) as File[];
    this.editCommentAttachments = [...this.editCommentAttachments, ...files];
  }

  // Save edited comment
  async saveEditComment() {
    if (!this.selectedTicket?.id || !this.editingCommentId || !this.editCommentMessage.trim()) return;
    if (this.editingComment) return; // Prevent double submission

    this.editingComment = true;
    try {
      const ticketRef = this.afs.collection('contacts').doc(this.selectedTicket.id);
      const ticketDoc = await ticketRef.get().toPromise();
      const ticketData = ticketDoc?.data() as Ticket;
      const comments = ticketData.comments || [];

      // Find the comment index - match by ID first, then by userId and createdAt
      let commentIndex = -1;
      
      if (this.editingCommentId.startsWith('edit_')) {
        // Extract userId and timestamp from editingCommentId
        const parts = this.editingCommentId.split('_');
        const userId = parts[1];
        const timestamp = parseInt(parts[2]);
        
        commentIndex = comments.findIndex((c: any) => {
          if (c.userId !== userId) return false;
          const cTime = this.getCommentTimestamp(c.createdAt);
          return cTime && Math.abs(cTime - timestamp) < 1000; // Within 1 second
        });
      } else {
        // Match by ID
        commentIndex = comments.findIndex((c: any) => c.id === this.editingCommentId);
      }

      if (commentIndex === -1) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Comment not found',
        });
        this.cancelEditComment();
        this.editingComment = false;
        return;
      }

      const comment = comments[commentIndex];

      // Upload new attachments if any
      let finalAttachments = [...this.editCommentExistingAttachments];
      if (this.editCommentAttachments.length > 0) {
        for (const file of this.editCommentAttachments) {
          const filePath = `tickets/${this.currentUser.uid}/comments/${Date.now()}_${file.name}`;
          const fileRef = this.storage.ref(filePath);
          const task = fileRef.put(file);
          await task.snapshotChanges().pipe(last(), takeUntil(this.destroy$)).toPromise();
          const url = await fileRef.getDownloadURL().toPromise();
          finalAttachments.push({
            url: url as string,
            name: file.name,
            size: file.size,
            type: file.type,
          });
        }
      }

      // Update the comment
      const updatedComment = {
        ...comment,
        message: this.editCommentMessage,
        attachments: finalAttachments,
        updatedAt: new Date(),
      };

      comments[commentIndex] = updatedComment;

      await ticketRef.update({
        comments: comments,
        updatedAt: new Date(),
      });

      // Update selectedTicket immediately
      if (this.selectedTicket) {
        this.selectedTicket.comments = comments;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Comment updated successfully',
      });
    } catch (error) {
      console.error('Error updating comment:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update comment',
      });
    } finally {
      // Always reset editing state
      this.cancelEditComment();
      this.editingComment = false;
    }
  }

  // Helper to get timestamp from various date formats
  getCommentTimestamp(date: any): number | null {
    if (!date) return null;
    if (date instanceof Date) return date.getTime();
    if (date?.toDate) return date.toDate().getTime();
    if (typeof date === 'number') return date;
    if (date?.seconds) return date.seconds * 1000;
    return null;
  }

  // Delete a comment
  async deleteComment(comment: TicketComment) {
    if (!this.selectedTicket?.id) return;
    const commentId = this.getCommentIdentifier(comment);
    if (this.deletingCommentId === commentId) return; // Prevent double submission

    // Confirm deletion
    if (!confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    this.deletingCommentId = commentId;
    try {
      const ticketRef = this.afs.collection('contacts').doc(this.selectedTicket.id);
      const ticketDoc = await ticketRef.get().toPromise();
      const ticketData = ticketDoc?.data() as Ticket;
      const comments = ticketData.comments || [];

      // Find and remove the comment - match by ID first, then by userId and createdAt
      const commentTime = this.getCommentTimestamp(comment.createdAt);
      const updatedComments = comments.filter((c: any) => {
        // Match by ID if available
        if (comment.id && c.id) {
          return c.id !== comment.id;
        }
        // Match by userId and createdAt timestamp
        if (c.userId === comment.userId && commentTime) {
          const cTime = this.getCommentTimestamp(c.createdAt);
          return !cTime || Math.abs(cTime - commentTime) > 1000; // More than 1 second difference
        }
        return true;
      });

      await ticketRef.update({
        comments: updatedComments,
        commentCount: updatedComments.length,
        updatedAt: new Date(),
      });

      // Update selectedTicket immediately
      if (this.selectedTicket) {
        this.selectedTicket.comments = updatedComments;
        this.selectedTicket.commentCount = updatedComments.length;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Comment deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete comment',
      });
    } finally {
      this.deletingCommentId = null;
    }
  }

  getAttachmentUrl(attachment: any): string {
    if (typeof attachment === 'string') {
      return attachment;
    }
    return attachment.url || attachment;
  }

  getAttachmentName(attachment: any): string {
    let fileName = '';
    
    if (typeof attachment === 'string') {
      // If it's just a URL string, extract filename from URL
      const urlParts = attachment.split('/');
      const urlFileName = urlParts[urlParts.length - 1];
      // Remove query parameters and decode URL encoding
      const decodedName = decodeURIComponent(urlFileName.split('?')[0]);
      // Extract actual filename if it contains timestamp prefix
      const parts = decodedName.split('_');
      if (parts.length > 1) {
        // Remove timestamp prefix (first part)
        fileName = parts.slice(1).join('_');
      } else {
        fileName = decodedName || 'Attachment';
      }
    } else {
      fileName = attachment.name || 'Attachment';
    }

    // Truncate to 20 characters, preserving extension
    if (fileName.length <= 20) {
      return fileName;
    }

    // Extract extension
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
      // No extension
      return fileName.substring(0, 20) + '...';
    }

    const nameWithoutExt = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);

    if (nameWithoutExt.length <= 20) {
      return fileName;
    }

    return nameWithoutExt.substring(0, 20) + '...' + extension;
  }

  getAttachmentSize(attachment: any): string {
    if (typeof attachment === 'string') {
      return '';
    }
    if (attachment.size) {
      const sizeInKB = attachment.size / 1024;
      if (sizeInKB < 1024) {
        return `${sizeInKB.toFixed(2)} KB`;
      }
      return `${(sizeInKB / 1024).toFixed(2)} MB`;
    }
    return '';
  }

  async updateTicketDetails() {
    if (!this.selectedTicket?.id) return;
    if (this.updatingTicket) return; // Prevent double submission

    this.updatingTicket = true;
    try {
      const updateData: any = {
        status: this.editTicketForm.status,
        priority: this.editTicketForm.priority,
        category: this.editTicketForm.category,
        updatedAt: new Date(),
      };

      // Add status change comment if status changed
      if (this.editTicketForm.status !== this.selectedTicket.status) {
        // Determine if user is admin
        const isAdmin = this.currentUser.role === 'superAdmin' || this.currentUser.role === 'admin';
        
        // Get user name based on role
        let userName = 'User';
        if (this.currentUser.role === 'company') {
          userName = this.currentUser.name || 'Company User';
        } else if (this.currentUser.fname || this.currentUser.lname) {
          userName = `${this.currentUser.fname || ''} ${this.currentUser.lname || ''}`.trim() || 'User';
        }

        // Get user avatar based on role
        let userAvatar: string | undefined;
        if (this.currentUser.role === 'company') {
          userAvatar = this.currentUser.logo;
        } else {
          userAvatar = this.currentUser.avatar || this.currentUser.photoURL;
        }

        const statusComment: any = {
          userId: this.currentUser.uid,
          userName: userName,
          message: `Status updated to: ${this.editTicketForm.status}`,
          createdAt: new Date(),
          isAdmin: isAdmin,
        };

        // Only add userAvatar if it exists
        if (userAvatar) {
          statusComment.userAvatar = userAvatar;
        }

        const ticketRef = this.afs.collection('contacts').doc(this.selectedTicket.id);
        const ticketDoc = await ticketRef.get().toPromise();
        const ticketData = ticketDoc?.data() as Ticket;
        const updatedComments = [...(ticketData.comments || []), statusComment];

        updateData.comments = updatedComments;
        updateData.commentCount = updatedComments.length;
      }

      await this.afs.collection('contacts').doc(this.selectedTicket.id).update(updateData);

      // Update the selectedTicket immediately to show changes in the modal
      if (this.selectedTicket) {
        this.selectedTicket.status = this.editTicketForm.status;
        this.selectedTicket.priority = this.editTicketForm.priority;
        this.selectedTicket.category = this.editTicketForm.category;
        this.selectedTicket.updatedAt = new Date();
        
        // Update comments if status changed
        if (updateData.comments) {
          this.selectedTicket.comments = updateData.comments;
          this.selectedTicket.commentCount = updateData.commentCount;
        }
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Ticket updated successfully',
      });

      // Reload tickets list in the background
      this.loadTickets();
    } catch (error) {
      console.error('Error updating ticket:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update ticket',
      });
    } finally {
      this.updatingTicket = false;
    }
  }

  // Utility methods
  formatDate(date: Date | any): string {
    if (!date) return '';
    const d = date instanceof Date ? date : date?.toDate ? date.toDate() : new Date(date);
    if (isNaN(d.getTime())) return '';
    return this.datePipe.transform(d, 'medium') || '';
  }

  getTimeAgo(date: Date | any): string {
    if (!date) return '';
    const d = date instanceof Date ? date : date?.toDate ? date.toDate() : new Date(date);
    if (isNaN(d.getTime())) return '';

    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return this.formatDate(d);
  }

  getStatusClass(status: string): string {
    const classes: { [key: string]: string } = {
      Open: 'bg-orange-100 text-orange-700',
      'In Progress': 'bg-yellow-100 text-yellow-700',
      Solved: 'bg-green-100 text-green-700',
      Closed: 'bg-gray-100 text-gray-700',
    };
    return classes[status] || 'bg-gray-100 text-gray-700';
  }

  getPriorityClass(priority: string): string {
    const classes: { [key: string]: string } = {
      Urgent: 'bg-red-100 text-red-700',
      High: 'bg-orange-100 text-orange-700',
      Normal: 'bg-blue-100 text-blue-700',
      Low: 'bg-gray-100 text-gray-700',
    };
    return classes[priority] || 'bg-gray-100 text-gray-700';
  }

  getPriorityIcon(priority: string): string {
    const icons: { [key: string]: string } = {
      Urgent: 'fa-exclamation-circle',
      High: 'fa-arrow-up',
      Normal: 'fa-minus',
      Low: 'fa-arrow-down',
    };
    return icons[priority] || 'fa-minus';
  }

  // Pagination
  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }

  // Bulk actions
  isTicketSelected(ticketId: string | undefined): boolean {
    return this.selectedTickets.some((t) => t.id === ticketId);
  }

  onCheckboxChange(ticket: Ticket, checked: boolean) {
    if (checked) {
      if (!this.selectedTickets.find((t) => t.id === ticket.id)) {
        this.selectedTickets.push(ticket);
      }
    } else {
      this.selectedTickets = this.selectedTickets.filter((t) => t.id !== ticket.id);
    }
    this.selectAll = this.selectedTickets.length === this.filteredTickets.length;
  }

  onSelectAllChange(checked: boolean) {
    if (checked) {
      this.selectedTickets = [...this.filteredTickets];
    } else {
      this.selectedTickets = [];
    }
    this.selectAll = checked;
  }

  exportTickets() {
    // Implement export functionality
    this.messageService.add({
      severity: 'info',
      summary: 'Export',
      detail: 'Export functionality will be implemented',
    });
  }

  isTicketClosed(): boolean {
    return this.selectedTicket?.status === 'Closed';
  }
}
